/* eslint-disable max-len */
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Divider } from '@mui/material';
import styled from 'styled-components';

import { ScrollViewport, visibleScrollbar } from 'components/ScrollControls';

export const STAGE_PILL_HEIGHT = 44;
// Below this the row scrolls instead of squeezing the pills further.
export const MIN_STAGE_PILL_WIDTH = 160;

export const ClosedStage = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  height: ${STAGE_PILL_HEIGHT}px;
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.palette.primary.main : theme.palette.common.white};
  border-radius: 30px;
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  padding: 0 24px;
  flex: 1 1 auto;
  min-width: ${MIN_STAGE_PILL_WIDTH}px;
  overflow: hidden;
`;

export const Name = styled.p<{ $isSelected?: boolean }>`
  color: ${({ $isSelected, theme }) => ($isSelected ? theme.palette.common.white : theme.palette.common.black)};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  margin-left: 8px;
`;

export const StagesViewport = styled(ScrollViewport)<{ $share: number }>`
  flex: ${({ $share }) => $share} 1 0;
`;

export const ClosedStageWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
`;

export const StyledDivider = styled(Divider)`
  height: 28px;
  margin: 8px 0;
`;

export const StagesRow = styled.div`
  display: flex;
  align-items: center;
  min-height: ${STAGE_PILL_HEIGHT}px;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  ${visibleScrollbar}
`;

export const StyledDragIndicatorIcon = styled(DragIndicatorIcon)<{ $isSelected: boolean }>`
  color: ${({ $isSelected, theme }) => ($isSelected ? theme.palette.common.white : theme.palette.grey[300])};
  cursor: grab;
`;

export const StyledSortableStage = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  height: ${STAGE_PILL_HEIGHT}px;
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.palette.primary.main : theme.palette.common.white};
  border-radius: 30px;
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  padding: 0 12px;
  cursor: pointer;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0; /* Allows flex items to shrink below their content size */
`;

// in future we may need the pointer box, but for now we don't need it
const PointerBox = styled.div`
  display: flex;
  height: 44px;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center;
  min-width: 120px;
  width: 100%;
  align-items: center;
  justify-content: center;

  /* Responsive design */
  @media (max-width: 768px) {
    min-width: 100px;
  }

  @media (max-width: 480px) {
    min-width: 80px;
  }
`;

export const RightPointerBox = styled(PointerBox)`
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 258 36" fill="none" preserveAspectRatio="none"><path d="M241.204 0C242.346 0 243.434 0.488605 244.193 1.34258L256.638 15.3428C257.985 16.8583 257.985 19.1417 256.638 20.6572L244.193 34.6574C243.434 35.5114 242.346 36 241.204 36H18C8.05888 36 0 27.9411 0 18C0 8.05888 8.05888 0 18 0H241.204Z" fill="%230772E5"/></svg>');
`;

export const DoublePointerBox = styled(PointerBox)`
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 254 36" fill="none" preserveAspectRatio="none"><mask id="path-1-inside-1_31004_264837" fill="white"><path d="M237.205 0C238.347 0 239.435 0.488591 240.194 1.34254L252.639 15.3425C253.986 16.8581 253.986 19.1419 252.639 20.6575L240.194 34.6575C239.435 35.5114 238.347 36 237.205 36H4.90836C1.45697 36 -0.374267 31.9221 1.91872 29.3425L9.63879 20.6575C10.9859 19.1419 10.9859 16.8581 9.63879 15.3425L1.91871 6.65746C-0.374268 4.07785 1.45696 0 4.90835 0H237.205Z"/></mask><path d="M237.205 0C238.347 0 239.435 0.488591 240.194 1.34254L252.639 15.3425C253.986 16.8581 253.986 19.1419 252.639 20.6575L240.194 34.6575C239.435 35.5114 238.347 36 237.205 36H4.90836C1.45697 36 -0.374267 31.9221 1.91872 29.3425L9.63879 20.6575C10.9859 19.1419 10.9859 16.8581 9.63879 15.3425L1.91871 6.65746C-0.374268 4.07785 1.45696 0 4.90835 0H237.205Z" fill="white"/><path d="M240.194 34.6575L240.942 35.3218L240.194 34.6575ZM252.639 15.3425L253.386 14.6782L252.639 15.3425ZM252.639 20.6575L253.386 21.3218L252.639 20.6575ZM240.194 1.34254L240.942 0.678181L240.194 1.34254ZM240.194 1.34254L239.447 2.00691L251.891 16.0069L252.639 15.3425L253.386 14.6782L240.942 0.678181L240.194 1.34254ZM252.639 20.6575L251.891 19.9931L239.447 33.9931L240.194 34.6575L240.942 35.3218L253.386 21.3218L252.639 20.6575ZM237.205 36V35H4.90836V36V37H237.205V36ZM1.91872 29.3425L2.66612 30.0069L10.3862 21.3218L9.63879 20.6575L8.89138 19.9931L1.17131 28.6782L1.91872 29.3425ZM9.63879 15.3425L10.3862 14.6782L2.66612 5.99309L1.91871 6.65746L1.17131 7.32182L8.89138 16.0069L9.63879 15.3425ZM4.90835 0V1H237.205V0V-1H4.90835V0ZM1.91871 6.65746L2.66612 5.99309C0.946387 4.05839 2.31981 1 4.90835 1V0V-1C0.594108 -1 -1.69492 4.09731 1.17131 7.32182L1.91871 6.65746ZM9.63879 20.6575L10.3862 21.3218C12.0701 19.4274 12.0701 16.5726 10.3862 14.6782L9.63879 15.3425L8.89138 16.0069C9.90175 17.1436 9.90175 18.8564 8.89138 19.9931L9.63879 20.6575ZM4.90836 36V35C2.31981 35 0.946391 31.9416 2.66612 30.0069L1.91872 29.3425L1.17131 28.6782C-1.69493 31.9027 0.594121 37 4.90836 37V36ZM240.194 34.6575L239.447 33.9931C238.878 34.6336 238.062 35 237.205 35V36V37C238.633 37 239.993 36.3893 240.942 35.3218L240.194 34.6575ZM252.639 15.3425L251.891 16.0069C252.902 17.1436 252.902 18.8564 251.891 19.9931L252.639 20.6575L253.386 21.3218C255.07 19.4274 255.07 16.5726 253.386 14.6782L252.639 15.3425ZM240.194 1.34254L240.942 0.678181C239.993 -0.389262 238.633 -1 237.205 -1V0V1C238.062 1 238.878 1.36644 239.447 2.00691L240.194 1.34254Z" fill="%23E0E0E0" mask="url(%23path-1-inside-1_31004_264837)"/></svg>');
`;

export const RightRoundedPointerBox = styled(PointerBox)`
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 239 36" fill="none" preserveAspectRatio="none"><mask id="path-1-inside-1_31004_264853" fill="white"><path d="M221.001 0C230.942 7.52486e-05 239.001 8.05892 239.001 18C239.001 27.9411 230.942 35.9999 221.001 36H4.90835C1.45696 36 -0.374267 31.9221 1.91872 29.3425L9.63879 20.6575C10.9859 19.1419 10.9859 16.8581 9.63879 15.3425L1.91871 6.65746C-0.374268 4.07785 1.45696 0 4.90835 0H221.001Z"/></mask><path d="M221.001 0C230.942 7.52486e-05 239.001 8.05892 239.001 18C239.001 27.9411 230.942 35.9999 221.001 36H4.90835C1.45696 36 -0.374267 31.9221 1.91872 29.3425L9.63879 20.6575C10.9859 19.1419 10.9859 16.8581 9.63879 15.3425L1.91871 6.65746C-0.374268 4.07785 1.45696 0 4.90835 0H221.001Z" fill="white"/><path d="M221.001 0V-1V0ZM221.001 36V37V36ZM221.001 0V1C230.39 1.00007 238.001 8.61121 238.001 18H239.001H240.001C240.001 7.50663 231.494 -0.999921 221.001 -1V0ZM239.001 18H238.001C238.001 27.3888 230.39 34.9999 221.001 35V36V37C231.494 36.9999 240.001 28.4934 240.001 18H239.001ZM221.001 36V35H4.90835V36V37H221.001V36ZM1.91872 29.3425L2.66612 30.0069L10.3862 21.3218L9.63879 20.6575L8.89138 19.9931L1.17131 28.6782L1.91872 29.3425ZM9.63879 15.3425L10.3862 14.6782L2.66612 5.99309L1.91871 6.65746L1.17131 7.32182L8.89138 16.0069L9.63879 15.3425ZM4.90835 0V1H221.001V0V-1H4.90835V0ZM1.91871 6.65746L2.66612 5.99309C0.946387 4.05839 2.31981 1 4.90835 1V0V-1C0.594108 -1 -1.69492 4.09731 1.17131 7.32182L1.91871 6.65746ZM9.63879 20.6575L10.3862 21.3218C12.0701 19.4274 12.0701 16.5726 10.3862 14.6782L9.63879 15.3425L8.89138 16.0069C9.90175 17.1436 9.90175 18.8564 8.89138 19.9931L9.63879 20.6575ZM4.90835 36V35C2.31981 35 0.946387 31.9416 2.66612 30.0069L1.91872 29.3425L1.17131 28.6782C-1.69492 31.9027 0.594107 37 4.90835 37V36Z" fill="%23E0E0E0" mask="url(%23path-1-inside-1_31004_264853)"/></svg>');
`;

export const DoubleRoundedPointerBox = styled(PointerBox)`
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 262 36" fill="none" preserveAspectRatio="none"><mask id="path-1-inside-1_31004_264870" fill="white"><path d="M244 0C253.941 0 262 8.05888 262 18C262 27.9411 253.941 36 244 36H18C8.05888 36 0 27.9411 0 18C0 8.05888 8.05888 4.63895e-06 18 0H244Z"/></mask><path d="M244 0C253.941 0 262 8.05888 262 18C262 27.9411 253.941 36 244 36H18C8.05888 36 0 27.9411 0 18C0 8.05888 8.05888 4.63895e-06 18 0H244Z" fill="white"/><path d="M18 36V37V36ZM18 0V-1V0ZM244 0V1C253.389 1 261 8.61116 261 18H262H263C263 7.50663 254.493 -1 244 -1V0ZM262 18H261C261 27.3888 253.389 35 244 35V36V37C254.493 37 263 28.4934 263 18H262ZM244 36V35H18V36V37H244V36ZM18 36V35C8.61116 35 1 27.3888 1 18H0H-1C-1 28.4934 7.50659 37 18 37V36ZM0 18H1C1 8.61116 8.61116 1 18 1V0V-1C7.50659 -0.999995 -1 7.50659 -1 18H0ZM18 0V1H244V0V-1H18V0Z" fill="%23E0E0E0" mask="url(%23path-1-inside-1_31004_264870)"/></svg>');
`;
